import { Button } from "@/components/ui/button";
import { FieldError, Field, FieldLabel } from "@/components/ui/field";
import { Heading } from "@/components/heading";
import { Input } from "@/components/ui/input";
import { Text, TextLink } from "@/components/text";
import {
	createFileRoute,
	useNavigate,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Spinner } from "@/components/ui/spinner";
import { useServerFn } from "@tanstack/react-start";
import { createAccount as createAccountHandler } from "@/handlers/createAccount";
import { Icon } from "@/components/icon";
import axios from "axios";
import { toast } from "sonner";
import { commitLocalUpdate } from "relay-runtime";
import { environment } from "@/relay/environment";
import { useState } from "react";
import { usePostHog } from "posthog-js/react";
import { trackSignup } from "@/analytics/auth";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";
import { withMask } from "use-mask-input";

export const Route = createFileRoute("/_auth/(no-auth)/cadastro")({
	component: RouteComponent,
	validateSearch: z.object({
		redirectTo: z.string().optional(),
	}),
	head: () => {
		return {
			meta: [
				{
					title: "Criar Conta | Scarf - Sua Área de Membros",
				},
				{
					name: "description",
					content:
						"Crie sua conta gratuita na Scarf e comece a construir sua área de membros personalizada para vender cursos, mentorias e muito mais.",
				},
				{
					name: "keywords",
					content:
						"criar conta scarf, área de membros, comunidade, cursos online, mentorias, plataforma de cursos, negócio digital, membership",
				},
				{
					property: "og:title",
					content: "Criar Conta | Scarf - Sua Área de Membros",
				},
				{
					property: "og:description",
					content:
						"Crie sua conta gratuita na Scarf e comece a construir sua área de membros personalizada para vender cursos, mentorias e muito mais.",
				},
				{
					property: "og:url",
					content: "https://painel.scarf.club/cadastro",
				},
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "twitter:title",
					content: "Criar Conta | Scarf - Sua Área de Membros",
				},
				{
					property: "twitter:description",
					content:
						"Crie sua conta gratuita na Scarf e comece a construir sua área de membros personalizada para vender cursos, mentorias e muito mais.",
				},
				{
					property: "twitter:url",
					content: "https://painel.scarf.club/cadastro",
				},
			],
		};
	},
});

function RouteComponent() {
	const { redirectTo } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const router = useRouter();
	const posthog = usePostHog();

	const [showPassword, setShowPassword] = useState(false);

	const createAccount = useServerFn(createAccountHandler);
	const form = useForm({
		defaultValues: {
			email: "",
			username: "",
			phone: "",
			password: "",
		},
		validators: {
			onChangeAsyncDebounceMs: 600,
			onChangeAsync: z
				.object({
					username: z
						.string()
						.min(
							2,
							"Nome de usuário precisa ter no mínimo 2 caracteres e conter apenas letras e números (sem espaços)",
						),
					email: z.string().email("Email inválido"),
					phone: z
						.string()
						.regex(/^\(\d{2}\)\s?\d{5}-\d{4}$/, "Telefone inválido"),
					password: z.string().min(1, "A senha é obrigatória"),
				})
				.superRefine(async (data, ctx) => {
					if (data.password.length < 8) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa ter no mínimo 8 caracteres",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one uppercase letter
					if (!/[A-Z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra maiúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one lowercase letter
					if (!/[a-z]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos uma letra minúscula",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one number
					if (!/\d/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message: "A senha precisa conter pelo menos um número",
							path: ["password"],
							input: data.password,
						});
					}

					// validate if password contains at least one special character
					if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(data.password)) {
						ctx.addIssue({
							code: "custom",
							message:
								"A senha precisa conter pelo menos um caractere especial",
							path: ["password"],
							input: data.password,
						});
					}

					const isUsernameValid = await axios
						.head(`${import.meta.env.VITE_SERVER_URL}/u/${data.username}`)
						.then(() => false)
						.catch(() => true);

					if (!isUsernameValid) {
						ctx.addIssue({
							code: "custom",
							message: "Esse nome de usuário já está em uso",
							path: ["username"],
							input: data.username,
						});
					}
				}),
		},
		onSubmit: async ({ value }) => {
			return await createAccount({ data: value })
				.then(async (data) => {
					commitLocalUpdate(environment, (store) => store.invalidateStore());

					if (data?.user) {
						trackSignup(posthog, data.user);
					}

					await router.invalidate();

					if (data) {
						navigate({
							to: redirectTo || "/",
						});
					}
				})
				.catch((err) => {
					posthog.capture("auth_signup_failed", {
						error_message: err.message?.includes("already exists")
							? "email_already_exists"
							: "unknown",
					});

					if (err.message.includes("already exists")) {
						toast.error("Já existe uma conta com esse email");
					} else {
						toast.error("Não foi possível criar uma conta no momento");
					}

					throw err;
				});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<Heading>Crie sua conta!</Heading>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="username">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Nome de Usuário</FieldLabel>
								<InputGroup>
									<InputGroupAddon>
										<Icon name="AtSign" className="size-4" />
									</InputGroupAddon>

									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="nome_de_usuario"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="email">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									placeholder="Email"
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="phone">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Whatsapp</FieldLabel>

								<Input
									id={field.name}
									name={field.name}
									ref={withMask("(99) 99999-9999", {
										showMaskOnHover: false,
									})}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									placeholder="(11) 91234-5678"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Senha</FieldLabel>

								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										placeholder="Senha"
										type={showPassword ? "text" : "password"}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											onClick={() => setShowPassword((p) => !p)}
										>
											<Icon name="Eye" />
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<Text className="text-sm text-muted-foreground">
						Ao criar a conta estou de acordo com os{" "}
						<TextLink href="https://www.scarf.club/termos-de-uso/">
							Termos de uso
						</TextLink>{" "}
						e{" "}
						<TextLink href="https://www.scarf.club/politica-de-privacidade/">
							Política de Privacidade
						</TextLink>
					</Text>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<Icon name="CircleCheck" className="size-4" />
									) : null}

									{!isSubmitSuccessful && (
										<>Criar Conta {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								<Text className="font-medium">
									Já tem uma conta? <TextLink to="/login">Faça Login</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
